import { useNavigate } from "react-router-dom";
import { ArrowLeft, Scale, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";
import { useQuery } from "@tanstack/react-query";
import homeService from "../../services/HomeService";
import WeightManagementCard from "../../components/HomePage/WeightManagementCard";
import { WEIGHT_MANAGEMENT_SCORE_BANDS } from "../../utils/weightManagementAboutContent";

const LEVER_META = [
  {
    weight: 30,
    label: "Energy Balance",
    description:
      "Are you eating more or fewer calories than you burn? A deficit scores high — this is the biggest single driver.",
    color: "text-blue-600 dark:text-blue-400",
    bgColor: "bg-blue-500",
    lightBg: "bg-blue-50 dark:bg-blue-950/30",
    border: "border-blue-200 dark:border-blue-800/40",
  },
  {
    weight: 15,
    label: "Meal Timing",
    description:
      "Last meal before 7 pm scores well. Eating at 10 pm scores poorly. Same food, worse timing = worse score.",
    color: "text-purple-600 dark:text-purple-400",
    bgColor: "bg-purple-500",
    lightBg: "bg-purple-50 dark:bg-purple-950/30",
    border: "border-purple-200 dark:border-purple-800/40",
  },
  {
    weight: 12,
    label: "Protein Intake",
    description:
      "Target 18%+ of calories from protein. Too little triggers passive overeating of carbs and fat.",
    color: "text-green-600 dark:text-green-400",
    bgColor: "bg-green-500",
    lightBg: "bg-green-50 dark:bg-green-950/30",
    border: "border-green-200 dark:border-green-800/40",
  },
  {
    weight: 10,
    label: "Sleep",
    description:
      "Short sleep raises hunger hormones. Under 6 hours can add up to 400 silent appetite calories per day.",
    color: "text-indigo-600 dark:text-indigo-400",
    bgColor: "bg-indigo-500",
    lightBg: "bg-indigo-50 dark:bg-indigo-950/30",
    border: "border-indigo-200 dark:border-indigo-800/40",
  },
  {
    weight: 10,
    label: "Meal Quality",
    description:
      "High BBS meals cause smaller insulin spikes — less fat deposited even at the same calorie level.",
    color: "text-amber-600 dark:text-amber-400",
    bgColor: "bg-amber-500",
    lightBg: "bg-amber-50 dark:bg-amber-950/30",
    border: "border-amber-200 dark:border-amber-800/40",
  },
  {
    weight: 8,
    label: "Gut Health",
    description:
      "A healthy gut produces natural GLP-1 (the same hormone Ozempic mimics), improving fullness signals.",
    color: "text-teal-600 dark:text-teal-400",
    bgColor: "bg-teal-500",
    lightBg: "bg-teal-50 dark:bg-teal-950/30",
    border: "border-teal-200 dark:border-teal-800/40",
  },
  {
    weight: 8,
    label: "Fat Cell Quality",
    description:
      "Are fat cells multiplying healthily or swelling dangerously? Most apps don't track this at all.",
    color: "text-orange-600 dark:text-orange-400",
    bgColor: "bg-orange-500",
    lightBg: "bg-orange-50 dark:bg-orange-950/30",
    border: "border-orange-200 dark:border-orange-800/40",
  },
  {
    weight: 7,
    label: "Body Trend",
    description:
      "Is your actual weight and waist moving in the right direction based on recent measurements?",
    color: "text-rose-600 dark:text-rose-400",
    bgColor: "bg-rose-500",
    lightBg: "bg-rose-50 dark:bg-rose-950/30",
    border: "border-rose-200 dark:border-rose-800/40",
  },
];

const SCORE_BANDS = WEIGHT_MANAGEMENT_SCORE_BANDS;

const WeightManagementDetail = () => {
  const navigate = useNavigate();

  const { data: homeData } = useQuery({
    queryKey: ["homePageData"],
    queryFn: () => homeService.getHomePageData(),
    staleTime: 5 * 60 * 1000,
  });

  const weightData = homeData?.["Weight Management"];

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{ minHeight: "100vh", maxHeight: "100vh", overflow: "hidden" }}
    >
      {/* Background blur */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{ backgroundImage: `url(${welcomeBg})` }}
      />
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 overflow-y-auto p-4 pb-24"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2 mb-5">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10 rounded-xl border-border bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title3 font-bold text-foreground">Weight Management</h1>
          <div className="h-10 w-10" />
        </div>

        <div className="space-y-4">

          {/* ── Live Weight Management Card (top) ── */}
          {weightData ? (
            <WeightManagementCard data={weightData} disableNavigation={true} />
          ) : (
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm flex items-center justify-center min-h-[120px]">
              <div className="text-center space-y-1.5">
                <div className="mx-auto h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Scale className="h-5 w-5 text-blue-500" />
                </div>
                <p className="text-subhead font-semibold text-foreground">No data yet</p>
                <p className="text-caption1 text-muted-foreground">Log meals to unlock your score</p>
              </div>
            </div>
          )}

          {/* ── What is the score? ── */}
          <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-950/40">
                <Scale className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-headline font-bold text-foreground leading-snug pt-1.5">
                What is the Weight Management Score?
              </h2>
            </div>
            <p className="text-callout leading-relaxed text-muted-foreground">
              A single number <span className="font-semibold text-foreground">(0–100)</span> that measures how well your current habits are set up for healthy weight management — not how much you weigh, but{" "}
              <span className="font-semibold text-foreground">where you are headed</span>.
            </p>
            {/* Weather forecast callout */}
            <div className="mt-3 rounded-xl border border-blue-200/60 bg-blue-50/70 dark:border-blue-800/30 dark:bg-blue-950/20 px-3 py-2.5">
              <p className="text-callout leading-relaxed text-blue-700 dark:text-blue-300">
                🌤️ Think of it like a <span className="font-bold">weather forecast for your weight</span>. It does not show what the weather is today — it shows where your habits are pointing you over the <span className="font-bold">next 4 weeks</span>.
              </p>
            </div>
          </div>

          {/* ── What it measures ── */}
          {/* <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
            <h2 className="text-headline font-bold text-foreground mb-4 flex items-center gap-2">
              <Info className="h-4 w-4 text-blue-500 shrink-0" />
              What it Measures
            </h2>
            <div className="space-y-2.5">
              {LEVER_META.map((lever) => (
                <div
                  key={lever.label}
                  className={`rounded-xl border p-3 ${lever.border} ${lever.lightBg}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`text-subhead font-bold ${lever.color}`}>{lever.label}</span>
                    <span className={`text-callout font-black ${lever.color}`}>{lever.weight}%</span>
                  </div>
                
                  <div className="mb-2 h-1.5 w-full overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
                    <div
                      className={`h-full rounded-full ${lever.bgColor}`}
                      style={{ width: `${lever.weight * 3.33}%` }}
                    />
                  </div>
                  <p className="text-footnote leading-relaxed text-muted-foreground">
                    {lever.description}
                  </p>
                </div>
              ))}
            </div>
          </div> */}

          <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
            <h2 className="text-headline font-bold text-foreground mb-4 flex items-center gap-2">
              <ChevronRight className="h-4 w-4 text-blue-500 shrink-0" />
              How to Read Your Score
            </h2>
            <div className="space-y-2.5">
              {SCORE_BANDS.map((band) => (
                <div
                  key={band.range}
                  className={`flex items-start gap-3 rounded-xl border p-3 ${band.bg} ${band.border}`}
                >
                  <div className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${band.dot}`} />
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-2 mb-0.5">
                      <span className={`text-subhead font-black ${band.color}`}>{band.range}</span>
                    </div>
                    <p className={`text-footnote font-semibold ${band.color}`}>{band.label}</p>
                    <p className="text-caption1 text-muted-foreground mt-0.5">{band.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      <BottomNavBar />
    </div>
  );
};

export default WeightManagementDetail;
